/**
 * Holidays dashboard section: list with create/edit slide panel.
 */

import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { HolidaysDataTable } from '@/components/holidays/HolidaysDataTable';
import { SlidePanel } from '@/components/common/SlidePanel';
import { CreateHolidayView } from '@/components/holidays/CreateHolidayView';
import { EditHolidayView } from '@/components/holidays/EditHolidayView';
import { useDetailsView } from '@/hooks/useDetailsView';
import { usePageActions } from '@/hooks/usePageActions';
import { CalendarSyncModal } from '@/components/calendar/CalendarSyncModal';
import { CALENDAR_SYNC_ENABLED } from '@/config/features';
import { Button } from '@/components/forms/Button';
import { RefreshCw } from 'lucide-react';
import type { ElementWidth } from '@/types/interface-elements.types';

/** Props for {@link HolidaysSection}. */
interface HolidaysSectionProps {
  onSuccess?: () => void;
  layoutWidth?: ElementWidth;
}

/** Dashboard widget: holidays list. */
export const HolidaysSection: React.FC<HolidaysSectionProps> = ({
  onSuccess,
  layoutWidth = 'full',
}) => {
  const queryClient = useQueryClient();
  const { canCreate, canEditRow, canDeleteRow, canExportCsv } = usePageActions('holidays');
  const { openRowId, openRow, closeRow } = useDetailsView();
  const [createFormOpen, setCreateFormOpen] = React.useState(false);
  const [editHolidayId, setEditHolidayId] = React.useState<string | null>(null);
  const [syncOpen, setSyncOpen] = React.useState(false);

  function handleOpenSync(): void {
    setSyncOpen(true);
  }

  function handleCloseSync(): void {
    setSyncOpen(false);
  }

  const handleCreateHoliday = () => {
    setCreateFormOpen(true);
    setEditHolidayId(null);
    closeRow();
  };

  const handleEditHoliday = (holidayId: string) => {
    setEditHolidayId(holidayId);
    setCreateFormOpen(false);
    closeRow();
  };

  return (
    <>
      <HolidaysDataTable
        showCreateButton={canCreate}
        onCreateHoliday={handleCreateHoliday}
        isSection={true}
        toolbarExtra={
          CALENDAR_SYNC_ENABLED ? (
            <Button
              preset="section-action"
              icon={RefreshCw}
              tooltipText="Synchroniser"
              aria-label="Synchroniser"
              onClick={handleOpenSync}
            />
          ) : undefined
        }
        layoutWidth={layoutWidth}
        expandedRowId={openRowId}
        onToggleRow={openRow}
        onCloseDetails={closeRow}
        onEditHoliday={handleEditHoliday}
        pageActions={{ canEditRow, canDeleteRow, canExportCsv }}
      />

      <SlidePanel isOpen={createFormOpen} onClose={() => setCreateFormOpen(false)}>
        <CreateHolidayView
          onClose={() => setCreateFormOpen(false)}
          onSuccess={() => {
            setCreateFormOpen(false);
            void queryClient.invalidateQueries({ queryKey: ['holidays'] });
            onSuccess?.();
          }}
        />
      </SlidePanel>

      <SlidePanel isOpen={editHolidayId !== null} onClose={() => setEditHolidayId(null)}>
        {editHolidayId && (
          <EditHolidayView
            key={editHolidayId}
            holidayId={editHolidayId}
            onClose={() => setEditHolidayId(null)}
            onSuccess={() => {
              setEditHolidayId(null);
              void queryClient.invalidateQueries({ queryKey: ['holidays'] });
              onSuccess?.();
            }}
          />
        )}
      </SlidePanel>
      {CALENDAR_SYNC_ENABLED ? (
        <CalendarSyncModal open={syncOpen} onClose={handleCloseSync} lockedTypes={['holiday']} />
      ) : null}
    </>
  );
};
